const typescriptCheatsheet = {
  id: "typescript",
  title: "TypeScript Cheatsheet",
  description:
    "A comprehensive guide to TypeScript including types, interfaces, classes, and advanced features",
  language: "typescript",
  category: "Programming & Frontend",
  level: "All Levels",
  sections: [
    {
      title: "Basic Types",
      items: [
        {
          title: "Primitive Types",
          content: "Core primitive types in TypeScript",
          code: `// Basic types
let isDone: boolean = false;
let decimal: number = 6;
let color: string = "blue";
let list: number[] = [1, 2, 3];
let tuple: [string, number] = ["hello", 10];
let nullValue: null = null;
let undefinedValue: undefined = undefined;
let anyValue: any = 4;
let voidValue: void = undefined;
let neverValue: never; // Function that never returns`,
          note: "TypeScript extends JavaScript's type system with additional primitives and type safety",
        },
        {
          title: "Type Assertions",
          content: "Ways to tell TypeScript about the type of a value",
          code: `// Angle-bracket syntax
let someValue: any = "this is a string";
let strLength: number = (<string>someValue).length;

// as syntax (preferred in JSX)
let otherValue: any = "another string";
let otherLength: number = (someValue as string).length;`,
          note: "Type assertions are like type casts in other languages but perform no special checking or restructuring of data",
        },
      ],
    },
    {
      title: "Interfaces",
      items: [
        {
          title: "Basic Interface",
          content: "Define object shapes using interfaces",
          code: `interface User {
  name: string;
  age: number;
  email?: string;  // Optional property
  readonly id: number;  // Read-only property
}

const user: User = {
  name: "John",
  age: 30,
  id: 1
};`,
          note: "Interfaces are fundamental building blocks in TypeScript, used to define contracts in your code",
        },
        {
          title: "Interface Extensions",
          content: "Interfaces can extend other interfaces",
          code: `interface Animal {
  name: string;
}

interface Bear extends Animal {
  honey: boolean;
}

const bear: Bear = {
  name: "Winnie",
  honey: true
};`,
          note: "Interfaces can extend multiple interfaces using comma separation",
        },
      ],
    },
    {
      title: "Functions",
      items: [
        {
          title: "Function Types",
          content: "Different ways to type functions",
          code: `// Function type expression
type MathFunc = (x: number, y: number) => number;

// Function with optional and default parameters
function buildName(firstName: string, lastName?: string): string {
  return lastName ? firstName + " " + lastName : firstName;
}

// Function overloads
function process(x: number): number;
function process(x: string): string;
function process(x: any): any {
  return x;
}`,
          note: "TypeScript provides multiple ways to annotate function parameters and return types",
        },
      ],
    },
    {
      title: "Classes",
      items: [
        {
          title: "Class Basics",
          content: "Object-oriented programming with TypeScript classes",
          code: `class Animal {
  private name: string;
  protected age: number;
  readonly species: string;

  constructor(name: string, age: number, species: string) {
    this.name = name;
    this.age = age;
    this.species = species;
  }

  public getName(): string {
    return this.name;
  }
}

class Dog extends Animal {
  bark(): void {
    console.log("Woof!");
  }
}`,
          note: "TypeScript adds type annotations and visibility modifiers to JavaScript classes",
        },
      ],
    },
    {
      title: "Generics",
      items: [
        {
          title: "Generic Types",
          content: "Create reusable components that work with multiple types",
          code: `// Generic function
function identity<T>(arg: T): T {
  return arg;
}

// Generic interface
interface Box<T> {
  contents: T;
}

// Generic class
class Container<T> {
  private value: T;

  constructor(value: T) {
    this.value = value;
  }

  getValue(): T {
    return this.value;
  }
}`,
          note: "Generics allow you to write flexible, reusable code that works with multiple types while maintaining type safety",
        },
      ],
    },
    {
      title: "Advanced Types",
      items: [
        {
          title: "Union and Intersection Types",
          content: "Combining types in different ways",
          code: `// Union type
type StringOrNumber = string | number;
let value: StringOrNumber = "hello";
value = 42; // Also valid

// Intersection type
type Employee = {
  name: string;
  id: number;
};

type Manager = {
  employees: Employee[];
};

type ManagerWithDetails = Employee & Manager;`,
          note: "Union types allow a value to be one of several types, while intersection types combine multiple types into one",
        },
        {
          title: "Mapped Types",
          content: "Transform the properties of a type",
          code: `// Make all properties optional
type Partial<T> = {
  [P in keyof T]?: T[P];
};

// Make all properties readonly
type Readonly<T> = {
  readonly [P in keyof T]: T[P];
};

// Example usage
interface Todo {
  title: string;
  description: string;
}

type PartialTodo = Partial<Todo>; // All fields optional
type ReadonlyTodo = Readonly<Todo>; // All fields readonly`,
          note: "Mapped types allow you to create new types based on old ones by transforming properties",
        },
      ],
    },
    {
      title: "Utility Types",
      items: [
        {
          title: "Built-in Utility Types",
          content: "TypeScript's built-in type transformations",
          code: `interface User {
  name: string;
  age: number;
  email: string;
}

// Partial - Makes all properties optional
type PartialUser = Partial<User>;

// Required - Makes all properties required
type RequiredUser = Required<User>;

// Pick - Picks specific properties
type NameAndEmail = Pick<User, 'name' | 'email'>;

// Omit - Removes specific properties
type UserWithoutEmail = Omit<User, 'email'>;

// Record - Creates a type with specified properties
type UserRoles = Record<'admin' | 'user', User>;`,
          note: "Utility types are built-in generic types that transform or extract properties from existing types",
        },
      ],
    },
    {
      title: "Type Guards",
      items: [
        {
          title: "Type Guards and Narrowing",
          content: "Runtime checks that guarantee the type in a scope",
          code: `// typeof type guard
function process(value: string | number) {
  if (typeof value === "string") {
    return value.toUpperCase(); // TypeScript knows value is string
  }
  return value.toFixed(2); // TypeScript knows value is number
}

// instanceof type guard
class Bird {
  fly() { console.log("flying..."); }
}

class Fish {
  swim() { console.log("swimming..."); }
}

function move(pet: Bird | Fish) {
  if (pet instanceof Bird) {
    pet.fly(); // TypeScript knows pet is Bird
  } else {
    pet.swim(); // TypeScript knows pet is Fish
  }
}

// Custom type guard
function isFish(pet: Bird | Fish): pet is Fish {
  return (pet as Fish).swim !== undefined;
}`,
          note: "Type guards help TypeScript narrow down the type of a variable within a conditional block",
        },
      ],
    },
    {
      title: "Decorators",
      items: [
        {
          title: "Class and Method Decorators",
          content: "Annotations that can modify classes and members",
          code: `// Method decorator
function log(target: any, key: string, descriptor: PropertyDescriptor) {
  // Store the original method
  const original = descriptor.value;

  // Replace the method with a new one
  descriptor.value = function(...args: any[]) {
    console.log(\`Calling \${key} with args: \${args}\`);
    return original.apply(this, args);
  };
}

class Calculator {
  @log
  add(x: number, y: number) {
    return x + y;
  }
}

// Class decorator
function sealed(constructor: Function) {
  Object.seal(constructor);
  Object.seal(constructor.prototype);
}

@sealed
class Example {
  // Class implementation
}`,
          note: "Decorators provide a way to add annotations and metadata to your classes and class members",
        },
      ],
    },
  ],
};

export default typescriptCheatsheet;
